/**
 * useSetupWizard Hook
 * State management for the setup wizard flow
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useProfile, type UserProfileExtended } from '@/hooks/useProfile';
import { useAuth } from '@/components/auth/AuthProvider';
import { useTenant } from '@/contexts/TenantContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/Toast';
import {
  type SetupWizardProgress,
  type SetupWizardStepId,
  type SetupProfileData,
  type SetupEntityData,
  SETUP_WIZARD_STEPS,
  DEFAULT_WIZARD_PROGRESS,
} from '../types';

interface UseSetupWizardResult {
  // State
  currentStep: number;
  totalSteps: number;
  currentStepConfig: typeof SETUP_WIZARD_STEPS[number];
  progress: SetupWizardProgress;
  isOpen: boolean;
  shouldShowWizard: boolean;

  // Navigation
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  skipWizard: () => void;
  completeWizard: () => Promise<void>;

  // Data
  profileData: Partial<SetupProfileData>;
  setProfileData: (data: Partial<SetupProfileData>) => void;
  entityData: SetupEntityData | null;
  setEntityData: (data: SetupEntityData | null) => void;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;

  // Profile
  profile: UserProfileExtended | undefined;
}

export function useSetupWizard(): UseSetupWizardResult {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const queryClient = useQueryClient();

  // Fetch profile
  const { profile, isLoading: profileLoading, updateProfile, isUpdating } = useProfile(user?.id || '');

  // Local state
  const [currentStep, setCurrentStep] = useState(0);
  const [profileData, setProfileDataState] = useState<Partial<SetupProfileData>>({});
  const [entityData, setEntityDataState] = useState<SetupEntityData | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Compute progress from profile
  const progress: SetupWizardProgress = useMemo(() => {
    if (profile?.setupWizardProgress) {
      return {
        ...DEFAULT_WIZARD_PROGRESS,
        ...profile.setupWizardProgress,
        completedSteps: (profile.setupWizardProgress.completedSteps || []) as SetupWizardStepId[],
      };
    }
    return DEFAULT_WIZARD_PROGRESS;
  }, [profile?.setupWizardProgress]);

  // Should show wizard if user hasn't completed setup
  const shouldShowWizard = useMemo(() => {
    if (!profile) return false;
    return !profile.hasCompletedSetup && !progress.skippedAt;
  }, [profile, progress.skippedAt]);

  // Auto-open wizard when profile loads
  useEffect(() => {
    if (!profileLoading && shouldShowWizard) {
      setIsOpen(true);
      // Restore saved step if any
      if (progress.currentStep > 0) {
        setCurrentStep(progress.currentStep);
      }
    }
  }, [profileLoading, shouldShowWizard, progress.currentStep]);

  // Initialize profile data from existing profile
  useEffect(() => {
    if (profile && !profileData.firstName) {
      setProfileDataState({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        companyName: profile.companyName || '',
        jobTitle: profile.jobTitle || '',
        timezone: profile.timezone || 'Europe/Zurich',
        language: (profile.language as SetupProfileData['language']) || 'de',
      });
    }
  }, [profile, profileData.firstName]);

  // Save progress to server
  const saveProgressMutation = useMutation({
    mutationFn: async (newProgress: Partial<SetupWizardProgress>) => {
      const response = await fetch('/api/profile', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setupWizardProgress: {
            ...progress,
            ...newProgress,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save progress');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
    },
  });

  // Navigation functions
  const nextStep = useCallback(() => {
    const newStep = Math.min(currentStep + 1, SETUP_WIZARD_STEPS.length - 1);
    setCurrentStep(newStep);

    // Save progress
    const currentStepId = SETUP_WIZARD_STEPS[currentStep].id;
    const completedSteps = progress.completedSteps.includes(currentStepId)
      ? progress.completedSteps
      : [...progress.completedSteps, currentStepId];

    saveProgressMutation.mutate({
      currentStep: newStep,
      completedSteps,
    });
  }, [currentStep, progress.completedSteps, saveProgressMutation]);

  const previousStep = useCallback(() => {
    const newStep = Math.max(currentStep - 1, 0);
    setCurrentStep(newStep);

    saveProgressMutation.mutate({
      currentStep: newStep,
    });
  }, [currentStep, saveProgressMutation]);

  const goToStep = useCallback((step: number) => {
    const validStep = Math.max(0, Math.min(step, SETUP_WIZARD_STEPS.length - 1));
    setCurrentStep(validStep);

    saveProgressMutation.mutate({
      currentStep: validStep,
    });
  }, [saveProgressMutation]);

  const skipWizard = useCallback(() => {
    saveProgressMutation.mutate({
      skippedAt: new Date().toISOString(),
    });
    setIsOpen(false);
  }, [saveProgressMutation]);

  // Complete wizard - save all data and mark complete
  const completeWizard = useCallback(async () => {
    try {
      // Update profile with collected data
      const response = await fetch('/api/profile', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...profileData,
          hasCompletedSetup: true,
          setupWizardProgress: {
            ...progress,
            currentStep: SETUP_WIZARD_STEPS.length - 1,
            completedSteps: SETUP_WIZARD_STEPS.map(s => s.id),
            entityCreated: !!entityData,
            firstEntityId: entityData ? entityData.uid : null,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to complete setup');
      }

      // Invalidate profile query
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });

      toast.success('Setup complete!', {
        description: 'Welcome to LegalSyncUp. You\'re all set to get started.',
      });

      setIsOpen(false);
    } catch (error) {
      console.error('Failed to complete wizard:', error);
      toast.error('Setup failed', {
        description: 'There was an error completing the setup. Please try again.',
      });
    }
  }, [profileData, entityData, progress, user?.id, queryClient]);

  // Data setters
  const setProfileData = useCallback((data: Partial<SetupProfileData>) => {
    setProfileDataState(prev => ({ ...prev, ...data }));
  }, []);

  const setEntityData = useCallback((data: SetupEntityData | null) => {
    setEntityDataState(data);
    if (data) {
      saveProgressMutation.mutate({
        entityCreated: true,
        firstEntityId: data.uid,
      });
    }
  }, [saveProgressMutation]);

  return {
    // State
    currentStep,
    totalSteps: SETUP_WIZARD_STEPS.length,
    currentStepConfig: SETUP_WIZARD_STEPS[currentStep],
    progress,
    isOpen,
    shouldShowWizard,

    // Navigation
    nextStep,
    previousStep,
    goToStep,
    skipWizard,
    completeWizard,

    // Data
    profileData,
    setProfileData,
    entityData,
    setEntityData,

    // Loading states
    isLoading: profileLoading,
    isSaving: saveProgressMutation.isPending || isUpdating,

    // Profile
    profile,
  };
}
